import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { GitPullRequest } from 'lucide-react';
import HandbookPolicyEditor from '../components/people/HandbookPolicyEditor';
import HandbookChangeRequestDiff from '../components/people/HandbookChangeRequestDiff';
import HandbookPolicyHistoryRail from '../components/people/HandbookPolicyHistoryRail';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import {
  canApproveHandbookChange,
  canAuthorEditChangeRequest,
  canDeleteHandbookChangeRequest,
  canProposeHandbookChange,
  resolveHandbookBriefingProfile,
} from '../utils/handbookPolicyAccess';
import {
  approveHandbookChangeRequest,
  backfillHandbookPolicyVersionIfEmpty,
  createHandbookChangeRequest,
  deleteHandbookChangeRequest,
  findActiveHandbookChangeRequestForAuthor,
  normalizeHandbookPolicyNumberParam,
  rejectHandbookChangeRequest,
  requestChangesHandbookChangeRequest,
  subscribeHandbookChangeRequests,
  subscribeHandbookPolicyByPolicyNumber,
  subscribeHandbookPolicyVersions,
} from '../utils/handbookPoliciesFirestore';
import { handbookChangeRequestDraftFromPolicy } from '../utils/handbookPolicyWorkflow';
import { sanitizeHandbookPolicyHtml } from '../utils/richComment';
import type { HandbookChangeRequest, HandbookPolicy, HandbookPolicyVersion } from '../types/handbookPolicy';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

const PeopleHandbookPolicyPage: React.FC = () => {
  const { policyId } = useParams<{ policyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const normalized = useMemo(() => normalizeHandbookPolicyNumberParam(policyId), [policyId]);
  const [policy, setPolicy] = useState<HandbookPolicy | null | undefined>(undefined);
  const [versions, setVersions] = useState<HandbookPolicyVersion[]>([]);
  const [changeRequests, setChangeRequests] = useState<HandbookChangeRequest[]>([]);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedCrId, setSelectedCrId] = useState<string | null>(searchParams.get('cr'));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [optimisticCr, setOptimisticCr] = useState<HandbookChangeRequest | null>(null);

  const briefingProfile = resolveHandbookBriefingProfile(user, crmUsers);
  const canPropose = canProposeHandbookChange({ authRole: user?.role, designations: user?.designations });
  const canApprove = canApproveHandbookChange({ authRole: user?.role, briefingProfile });
  const actor = useMemo(
    () => (user ? { uid: user.id, name: user.name || user.email || 'User' } : null),
    [user],
  );

  useEffect(() => {
    if (!normalized || !user?.id) {
      setPolicy(undefined);
      return;
    }
    setPolicy(undefined);
    setLoadError(null);
    setEditing(false);
    const unsub = subscribeHandbookPolicyByPolicyNumber(
      normalized,
      p => {
        setPolicy(p);
        setLoadError(null);
      },
      err => setLoadError(err),
      { userId: user.id, role: user.role, email: user.email ?? undefined },
    );
    return unsub;
  }, [normalized, user?.id, user?.role, user?.email]);

  useEffect(() => {
    if (!policy?.id || !user?.id) {
      setVersions([]);
      setChangeRequests([]);
      return;
    }
    const authMeta = { userId: user.id, role: user.role, email: user.email ?? undefined };
    const unsubV = subscribeHandbookPolicyVersions(
      policy.id,
      setVersions,
      () => setVersions([]),
      authMeta,
    );
    const unsubC = subscribeHandbookChangeRequests(
      policy.id,
      list => {
        setChangeRequests(list);
        setOptimisticCr(prev => (prev && list.some(cr => cr.id === prev.id) ? null : prev));
      },
      () => setChangeRequests([]),
      authMeta,
    );
    return () => {
      unsubV();
      unsubC();
    };
  }, [policy?.id, user?.id, user?.role, user?.email]);

  useEffect(() => {
    if (!policy || !actor || versions.length > 0) return;
    void backfillHandbookPolicyVersionIfEmpty(policy, actor).catch(() => undefined);
  }, [policy, actor, versions.length]);

  useEffect(() => {
    const fromQuery = searchParams.get('cr');
    if (fromQuery && fromQuery !== selectedCrId) setSelectedCrId(fromQuery);
  }, [searchParams, selectedCrId]);

  const selectedVersion = versions.find(v => v.id === selectedVersionId) || null;
  const selectedCr =
    changeRequests.find(cr => cr.id === selectedCrId)
    || (optimisticCr && optimisticCr.id === selectedCrId ? optimisticCr : null);
  const viewingPublished = !selectedVersion && !selectedCr;

  const openSuggest = async () => {
    if (!policy || !actor || !canPropose) return;
    setActionError(null);
    const existing = findActiveHandbookChangeRequestForAuthor(changeRequests, actor.uid);
    if (existing) {
      setSelectedCrId(existing.id);
      setSelectedVersionId(null);
      setEditing(canAuthorEditChangeRequest(existing.status));
      setSearchParams({ cr: existing.id }, { replace: true });
      return;
    }
    setBusy(true);
    try {
      const id = await createHandbookChangeRequest({ policy, actor });
      const seed = handbookChangeRequestDraftFromPolicy(policy);
      setOptimisticCr({
        id,
        policyId: policy.id,
        policyNumber: policy.policyNumber,
        status: 'draft',
        authorUid: actor.uid,
        authorName: actor.name,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...seed,
      });
      setSelectedCrId(id);
      setSelectedVersionId(null);
      setEditing(true);
      setSearchParams({ cr: id }, { replace: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not open a change request.');
    } finally {
      setBusy(false);
    }
  };

  const onSelectVersion = (versionId: string | null) => {
    setSelectedVersionId(versionId);
    setSelectedCrId(null);
    setEditing(false);
    setSearchParams({}, { replace: true });
  };

  const onSelectChangeRequest = (crId: string) => {
    setSelectedCrId(crId);
    setSelectedVersionId(null);
    setEditing(false);
    setSearchParams({ cr: crId }, { replace: true });
  };

  const canDeleteCr = (cr: HandbookChangeRequest) =>
    canDeleteHandbookChangeRequest({
      status: cr.status,
      authorUid: cr.authorUid,
      actorUid: actor?.uid,
      isCeo: canApprove,
    });

  const removeChangeRequest = async (cr: HandbookChangeRequest) => {
    if (!actor || !policy) return;
    if (!window.confirm('Delete this change request? This cannot be undone.')) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteHandbookChangeRequest({
        policyId: policy.id,
        changeRequest: cr,
        actor,
        isCeo: canApprove,
      });
      if (selectedCrId === cr.id) {
        setSelectedCrId(null);
        setEditing(false);
        setSearchParams({}, { replace: true });
      }
      setOptimisticCr(prev => (prev?.id === cr.id ? null : prev));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not delete this change request.');
    } finally {
      setBusy(false);
    }
  };

  if (!normalized) {
    return <Navigate to="/people/handbook" replace />;
  }

  if (policy === undefined && !loadError) {
    return (
      <div className="people-hub-route-root people-hub-handbook-route-root">
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="people-hub-route-root people-hub-handbook-route-root">
        <div className="ops-hub-toolbar">
          <div className="ops-hub-toolbar-left people-handbook-policy-toolbar">
            <Link to="/people/handbook" className="ops-hide-completed-btn people-handbook-back-link">
              ← Master Policy Index
            </Link>
          </div>
        </div>
        <div className="people-hub-body pm-scroll">
          <p className="people-hub-muted">Could not load this policy.</p>
          <p className="people-hub-muted people-handbook-error-detail">{loadError.message}</p>
        </div>
      </div>
    );
  }

  if (policy === null) {
    return <Navigate to="/people/handbook" replace />;
  }

  if (!policy) {
    return (
      <div className="people-hub-route-root people-hub-handbook-route-root">
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  const display = selectedCr
    ? {
        title: selectedCr.title,
        currentVersion: selectedCr.baseVersion,
        effectiveDate: selectedCr.effectiveDate,
        location: selectedCr.location,
        body: selectedCr.body,
        bodyFormat: selectedCr.bodyFormat,
      }
    : selectedVersion
      ? {
          title: selectedVersion.title,
          currentVersion: selectedVersion.version,
          effectiveDate: selectedVersion.effectiveDate,
          location: selectedVersion.location,
          body: selectedVersion.body,
          bodyFormat: selectedVersion.bodyFormat,
        }
      : policy;

  const bodyHtml =
    display.bodyFormat === 'html' && display.body?.trim() ? sanitizeHandbookPolicyHtml(display.body) : '';
  const plainBody = display.bodyFormat === 'plain' && display.body?.trim() ? display.body.trim() : '';
  const canEditSelectedCr = Boolean(
    selectedCr && actor && selectedCr.authorUid === actor.uid && canAuthorEditChangeRequest(selectedCr.status),
  );
  const ceoCanReview = Boolean(canApprove && selectedCr && selectedCr.status === 'open' && !editing);

  const runCeoAction = async (action: 'approve' | 'changes' | 'reject') => {
    if (!actor || !selectedCr) return;
    setBusy(true);
    setActionError(null);
    try {
      if (action === 'approve') {
        await approveHandbookChangeRequest({
          policy,
          changeRequest: selectedCr,
          actor,
          isCeo: canApprove,
          reviewNote,
        });
      } else if (action === 'changes') {
        await requestChangesHandbookChangeRequest({
          policyId: policy.id,
          changeRequest: selectedCr,
          actor,
          isCeo: canApprove,
          reviewNote,
        });
      } else {
        await rejectHandbookChangeRequest({
          policyId: policy.id,
          changeRequest: selectedCr,
          actor,
          isCeo: canApprove,
          reviewNote,
        });
      }
      setEditing(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update this change request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="people-hub-route-root people-hub-handbook-route-root">
      <div className="ops-hub-toolbar people-handbook-policy-bar">
        <div className="ops-hub-toolbar-left">
          <Link to="/people/handbook" className="ops-hide-completed-btn people-handbook-back-link">
            ← Index
          </Link>
          <h2 className="ops-hub-title people-handbook-policy-doc-title">
            <span className="people-handbook-index-policy-num">{policy.policyNumber}</span>
            {display.title}
          </h2>
          <div className="ops-hub-stats people-handbook-policy-stats">
            {display.currentVersion ? (
              <span className="ops-stat">{selectedCr ? `base v${display.currentVersion}` : `v${display.currentVersion}`}</span>
            ) : null}
            {display.effectiveDate ? <span className="ops-stat">{display.effectiveDate}</span> : null}
            {display.location ? <span className="ops-stat">{display.location}</span> : null}
            {policy.category ? <span className="ops-stat">{policy.category}</span> : null}
            {selectedCr ? <span className="ops-stat ops-stat-action">Change request</span> : null}
            {selectedVersion && selectedVersion.version !== policy.currentVersion ? (
              <span className="ops-stat">Historical snapshot</span>
            ) : null}
            {viewingPublished && !bodyHtml && !plainBody && !editing ? (
              <span className="ops-stat ops-stat-action">No body yet</span>
            ) : null}
          </div>
        </div>
        {canPropose && !editing ? (
          <div className="ops-hub-toolbar-right">
            <button type="button" className="ops-new-btn" onClick={() => void openSuggest()} disabled={busy}>
              <GitPullRequest size={14} aria-hidden />
              Suggest a change
            </button>
          </div>
        ) : null}
      </div>

      <div className="people-hub-body pm-scroll">
        <div className="people-handbook-catalog">
          <div className="people-handbook-catalog-main">
            {actionError ? <p className="people-handbook-editor-error">{actionError}</p> : null}

            {editing && selectedCr && actor ? (
              <HandbookPolicyEditor
                key={selectedCr.id}
                policy={policy}
                changeRequest={selectedCr}
                actor={actor}
                isCeo={canApprove}
                onCancel={() => setEditing(false)}
                onSaved={() => setEditing(false)}
                onDeleted={() => {
                  setEditing(false);
                  setSelectedCrId(null);
                  setSearchParams({}, { replace: true });
                }}
              />
            ) : (
              <>
                {selectedCr ? (
                  <HandbookChangeRequestDiff
                    proposed={selectedCr}
                    base={policy}
                    authorName={selectedCr.authorName}
                  />
                ) : bodyHtml ? (
                  <article className="people-handbook-article">
                    <div className="people-handbook-crm-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                  </article>
                ) : plainBody ? (
                  <article className="people-handbook-article">
                    <div className="people-handbook-crm-body people-handbook-crm-body-plain">
                      {plainBody.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  </article>
                ) : (
                  <div className="people-handbook-empty-copy">
                    <p className="people-hub-muted">
                      This policy is in the catalog but has no body text yet.
                      {canPropose ? ' Open a change request to add the full document.' : ''}
                    </p>
                  </div>
                )}

                {selectedCr && (canEditSelectedCr || canDeleteCr(selectedCr)) ? (
                  <div className="people-handbook-editor-actions">
                    {canEditSelectedCr ? (
                      <button type="button" className="ops-new-btn" onClick={() => setEditing(true)}>
                        Continue editing
                      </button>
                    ) : null}
                    {canDeleteCr(selectedCr) ? (
                      <button
                        type="button"
                        className="ops-hide-completed-btn people-handbook-delete-btn"
                        disabled={busy}
                        onClick={() => void removeChangeRequest(selectedCr)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {ceoCanReview ? (
                  <div className="people-handbook-review-panel">
                    <h3 className="people-handbook-rail-heading">CEO review</h3>
                    <label className="people-handbook-editor-field">
                      <span>Review note</span>
                      <textarea
                        className="ops-modal-input people-handbook-review-note"
                        value={reviewNote}
                        onChange={e => setReviewNote(e.target.value)}
                        rows={3}
                        placeholder="Optional note for the author"
                      />
                    </label>
                    <div className="people-handbook-editor-actions">
                      <button type="button" className="ops-hide-completed-btn" onClick={() => void runCeoAction('reject')} disabled={busy}>
                        Reject
                      </button>
                      <button type="button" className="ops-hide-completed-btn" onClick={() => void runCeoAction('changes')} disabled={busy}>
                        Request changes
                      </button>
                      <button type="button" className="ops-new-btn" onClick={() => void runCeoAction('approve')} disabled={busy}>
                        Approve & merge
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <HandbookPolicyHistoryRail
            versions={versions}
            changeRequests={changeRequests}
            currentVersion={policy.currentVersion}
            selectedVersionId={selectedVersionId}
            selectedChangeRequestId={selectedCrId}
            onSelectVersion={onSelectVersion}
            onSelectChangeRequest={onSelectChangeRequest}
            canDeleteChangeRequest={canDeleteCr}
            onDeleteChangeRequest={cr => void removeChangeRequest(cr)}
          />
        </div>
      </div>
    </div>
  );
};

export default PeopleHandbookPolicyPage;
